// AUTO-GENERATED by scripts/generateSupplementPreviews.ts. Do not edit by hand.
// To refresh: `npx tsx scripts/generateSupplementPreviews.ts` (also runs as
// part of `npm run download-supplements` and `npm run build`).

export type SupplementHeading = { level: number; text: string }
export type SupplementPreview = { paragraph: string; headings: SupplementHeading[] }

export const SUPPLEMENT_PREVIEWS: Record<string, SupplementPreview> = {
  "ai-for-epistemics": {
    paragraph: "Humanity’s epistemics, by which I mean our ability to come to true beliefs and thus make sensible decisions, is crucially important for achieving a great future. As AI capabilities improve throughout Plan A, they shape increasingly more of every facet of life, and epistemics is no different. I expect how AIs affect epistemics to be highly important, and it could easily be positive or negative.",
    headings: [
      { level: 3, text: "Background on AI for epistemics and the “basin of sanity”" },
      { level: 3, text: "Areas of impact of AI for epistemics" },
      { level: 3, text: "Indicators to track society’s epistemics" },
      { level: 3, text: "How transparent each AI project and government is to the public." },
      { level: 3, text: "Example interventions" },
    ],
  },
  "alignment-roadmap": {
    paragraph: ":::box DISCLAIMER: This supplement is a very rough draft representing in progress thinking. It should be interpreted as internal research notes, not as a final product that we are entirely happy with. This content is dense, full of not-very-well explained jargon, and unpolished. We decided to publish anyway in the spirit of transparency.",
    headings: [
      { level: 1, text: "Research Areas" },
      { level: 1, text: "Phase 1: Preparation" },
      { level: 1, text: "Phase 2: Mitigation + Elicitation" },
      { level: 1, text: "Phase 3: Pause" },
      { level: 1, text: "Phase 4: Handoff" },
      { level: 1, text: "Alignment strategy under different plans" },
      { level: 1, text: "Appendix: Regimes for misalignment: Schemeria, Hackistan, etc" },
      { level: 1, text: "Appendix: Capability Threshold Definitions" },
      { level: 1, text: "Appendix: Other definitions" },
      { level: 1, text: "Appendix: How this plan differs from other alignment plans" },
    ],
  },
  "capability-scaling-strategy": {
    paragraph: ":::PlanAvsPlanDUpliftShaped",
    headings: [
      { level: 1, text: "Overview" },
      { level: 1, text: "High level strategy" },
      { level: 1, text: "Costs of taking more time to scale" },
      { level: 1, text: "Stage 1: Scale to max-controllable-AI" },
      { level: 1, text: "Stage 2: Slow down nearly to a halt, then eventually hand off to AIs" },
    ],
  },
  "comparing-possible-plans": {
    paragraph: "In this supplement I’ll estimate how well each plan does on various intermediate metrics, and its chance of leading to a great future. I discuss quantitative metrics for the sake of concreteness, however I’m very much not confident in my precise estimates. I am confident in the conclusion that Plan A is substantially better than Plans B-D. For plans that I think could be competitive with Plan A, see this table.",
    headings: [
      { level: 1, text: "Introduction" },
      { level: 1, text: "Plan assessment table" },
      { level: 1, text: "Comparing authors’ views on plan likelihood and outcomes" },
      { level: 1, text: "Other plans" },
      { level: 1, text: "Classifying trajectories into plans" },
    ],
  },
  "covert-ai-projects": {
    paragraph: "In the Plan A scenario, we propose international coordination to develop AI safely. Keeping risk at an acceptable level requires substantial slowdowns in the pace of AI development. A central blocker for this type of coordination is the risk that one party develops AI in secret, thus gaining an advantage.",
    headings: [
      { level: 1, text: "Section 1: Overview of our strategy for covert project mitigation" },
      { level: 1, text: "Section 2: Preventing covert compute procurement" },
      { level: 1, text: "Section 3: Detecting covert AI infrastructure" },
      { level: 1, text: "Section 4: Limiting Consortium uplift to covert projects" },
      { level: 1, text: "Section 5: When is a covert project catastrophic?" },
      { level: 1, text: "Section 6: Overall risk estimate" },
      { level: 1, text: "Section 7: Covert project response plan" },
    ],
  },
  "deal-decline": {
    paragraph: "A consideration pushing toward scaling faster in Plan A is deal decline: the possibility of the deal either dissolving or becoming substantially less effective (which we call deal impairment).",
    headings: [
      { level: 1, text: "Appendix: Claude’s analysis of base rates" },
    ],
  },
  "faq": {
    paragraph: "A: No, Plan A reduces COP risks by a lot. The status quo involves an insane amount of power concentrating into the frontier AI companies (which are all based in the US). If they solve the alignment problem, the result will be a handful of AI CEOs or USG officials controlling an army of superintelligences, and hence having the ability to take over the world. (This is what happened in the slowdown ending of AI 2027).",
    headings: [
      { level: 3, text: "Q: Isn’t Plan A bad for concentration of power risks?" },
      { level: 3, text: "Q: Wouldn’t China not agree to the deal? Even if they did agree on paper, wouldn’t they defect and keep developing AI anyways?" },
      { level: 3, text: "Q: Isn’t Total Research Transparency bad because it leaks algorithmic secrets to China and other AI companies?" },
      { level: 3, text: "Q: Plan A is complicated. Shouldn’t we do a simpler and more straightforward plan, like shutting it all down (Plan S)?" },
      { level: 3, text: "Q: The US government is incompetent. Why are we trusting the US government to do a good job regulating AI?" },
      { level: 3, text: "Q: Plan A just isn’t going to happen. Don’t you know that?" },
    ],
  },
  "how-plan-a-solves-our-5-biggest-problems": {
    paragraph: "Due to the slowdown agreement, the intelligence explosion happens over the course of at least 10 years. Instead of being forced by competitive pressures to automate work and trust AIs, companies now are forced to slow down until they have a rock solid safety case. In the Plan A scenario, we have ~5 years with top expert level AI before handoff, and we could realistically get more time. If alignment ends up being hard enough that a 10 year slowdown isn’t sufficient, the infrastructure for a longer pause is set up.",
    headings: [
      { level: 3, text: "Problem #1: Loss of Control to superintelligent AIs." },
      { level: 3, text: "Problem #2: AI will lead to massive concentration of power into whatever group controls the first superintelligences (if anyone)." },
      { level: 3, text: "Problem #3: World War 3 risk due to superintelligences disempowering many countries, including nuclear powers." },
      { level: 3, text: "Problem #4: AI taking everyone’s jobs." },
      { level: 3, text: "Problem #5: AI misuse by small actors." },
    ],
  },
  "plan-a-assumptions": {
    paragraph: "Plan A is a scenario: it combines forecasts with policy recommendations. Some of the forecasts and recommendations we are confident in; others are just guesses we made because we had to, in order to achieve the desired level of concreteness.",
    headings: [
      { level: 1, text: "Empirical Updates" },
      { level: 1, text: "Policy Updates" },
    ],
  },
  "space-governance-plan": {
    paragraph: "It should be possible for almost all humans to get almost everything that they want from a post-ASI world. With huge numbers of robots capable of almost any task a human can do, it will be possible to provide everyone with the living standard that only the richest people today enjoy. People could spend their time doing what’s most meaningful to them—raising children, creating art, or spending time with friends. With ASI-driven advances in medicine, we’ll be able to treat mental illness, chronic pain, aging, cancer, and other diseases that shorten or worsen people’s lives today. In short, if we achieve the future described in Plan A, there will be enough to go around to give everyone a very good life. Many of the tough tradeoffs that are the focus of modern politics will be resolved. So in some sense, these decisions need not be extremely fraught.",
    headings: [
      { level: 1, text: "Preliminaries" },
      { level: 1, text: "Summary of the most promising options for allocating space resources" },
      { level: 1, text: "Some available options" },
      { level: 1, text: "Other decision points" },
      { level: 1, text: "Takes" },
    ],
  },
  "takeoff-forecast": {
    paragraph: "This supplement explains how we forecast AI capabilities in various parts of Plan A. We modified the AI Futures Model to simulate four situations:",
    headings: [
      { level: 1, text: "Modifications to the core model" },
      { level: 1, text: "Modeling the consortium trajectory" },
      { level: 1, text: "Modeling covert projects" },
      { level: 1, text: "Modeling deal dissolution" },
    ],
  },
  "transparency-plan": {
    paragraph: "AGI projects face key decisions around transparency: who gets to see algorithmic secrets, who gets real time access to the datacenters sufficient to be confident in what they are doing, who gets to be aware of ongoing experiments and training runs, and with what latency. This document outlines our current thinking on transparency in the context of Plan A.",
    headings: [
      { level: 1, text: "Baseline Transparency Desiderata" },
      { level: 1, text: "Four Transparency Proposals" },
      { level: 1, text: "Total Research Transparency" },
      { level: 1, text: "Filtered Transparency" },
      { level: 1, text: "When is each proposal desirable?" },
    ],
  },
}
